import axios, { type AxiosRequestConfig, type AxiosResponse, type AxiosError } from 'axios'
import { ErrorHandler } from '../errors'
import type { ApiEndpointKey } from './endpoints'
import { getApiBaseUrl, getApiBaseUrlFromConfig, DEFAULT_REQUEST_CONFIG, type ApiEndpointsConfig } from './endpoints'

/**
 * Конфигурация для API клиента
 */
export interface ApiClientConfig {
  baseUrl?: string
  endpoint?: ApiEndpointKey
  timeout?: number
  retries?: number
  retryDelay?: number
  headers?: Record<string, string>
  onRequest?: (config: AxiosRequestConfig) => void | Promise<void>
  onResponse?: <T = unknown>(response: AxiosResponse<T>) => void | Promise<void>
  onError?: (error: unknown) => void | Promise<void>
}

/**
 * Опции для запроса с поддержкой AbortController
 */
export interface ApiRequestOptions extends Omit<AxiosRequestConfig, 'signal'> {
  signal?: AbortSignal
  abortController?: AbortController
  endpoint?: ApiEndpointKey
}

/**
 * API клиент с поддержкой AbortController и обработкой ошибок
 */
export class ApiClient {
  private baseUrl?: string
  private defaultHeaders: Record<string, string>
  private authToken?: string
  private timeout: number
  private retries: number
  private retryDelay: number
  private onRequest?: (config: AxiosRequestConfig) => void | Promise<void>
  private onResponse?: <T = unknown>(response: AxiosResponse<T>) => void | Promise<void>
  private onError?: (error: unknown) => void | Promise<void>

  constructor(config: ApiClientConfig = {}) {
    // Приоритет: baseUrl > endpoint (из runtimeConfig)
    if (config.baseUrl) {
      // Если baseUrl передан напрямую - используем его
      this.baseUrl = config.baseUrl
    } else if (config.endpoint) {
      // Если передан endpoint, пытаемся получить URL из runtimeConfig (работает только в Vue контексте)
      try {
        this.baseUrl = getApiBaseUrl(config.endpoint)
      } catch {
        // Если useRuntimeConfig недоступен, baseUrl останется undefined
        // и будет использован endpoint при запросе
      }
    } else {
      // Если ничего не передано - baseUrl остается undefined
      this.baseUrl = undefined
    }
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      ...config.headers,
    }
    
    // Если токен передан в headers, сохраняем его
    if (config.headers?.Authorization) {
      this.authToken = config.headers.Authorization
    }
    this.timeout = config.timeout ?? DEFAULT_REQUEST_CONFIG.timeout
    this.retries = config.retries ?? DEFAULT_REQUEST_CONFIG.retries
    this.retryDelay = config.retryDelay ?? DEFAULT_REQUEST_CONFIG.retryDelay
    this.onRequest = config.onRequest
    this.onResponse = config.onResponse
    this.onError = config.onError
  }

  /**
   * Устанавливает токен авторизации
   * @param token - JWT токен или другой токен авторизации
   * @param prefix - Префикс для токена (по умолчанию 'Bearer')
   */
  setAuthToken(token: string | null, prefix: string = 'Bearer'): void {
    if (token) {
      this.authToken = prefix ? `${prefix} ${token}` : token
      this.defaultHeaders['Authorization'] = this.authToken
    } else {
      delete this.defaultHeaders['Authorization']
      this.authToken = undefined
    }
  }

  /**
   * Получить текущий токен авторизации
   */
  getAuthToken(): string | undefined {
    return this.authToken
  }

  /**
   * Удалить токен авторизации
   */
  clearAuthToken(): void {
    this.setAuthToken(null)
  }

  /**
   * Установить baseURL для клиента
   * @param baseUrl - Базовый URL для всех запросов
   */
  setBaseUrl(baseUrl: string | undefined): void {
    this.baseUrl = baseUrl
  }

  /**
   * Получить текущий baseURL
   */
  getBaseUrl(): string | undefined {
    return this.baseUrl
  }

  /**
   * Создает новый экземпляр клиента с переопределенными настройками
   */
  create(config: ApiClientConfig = {}): ApiClient {
    return new ApiClient({
      baseUrl: this.baseUrl,
      headers: this.defaultHeaders,
      timeout: this.timeout,
      retries: this.retries,
      retryDelay: this.retryDelay,
      onRequest: this.onRequest,
      onResponse: this.onResponse,
      onError: this.onError,
      ...config,
    })
  }

  /**
   * Выполняет GET запрос
   */
  async get<T = unknown>(
    url: string,
    options: ApiRequestOptions = {}
  ): Promise<T> {
    return this.request<T>('GET', url, options)
  }

  /**
   * Выполняет POST запрос
   */
  async post<T = unknown>(
    url: string,
    body?: Record<string, any> | BodyInit | null,
    options: ApiRequestOptions = {}
  ): Promise<T> {
    return this.request<T>('POST', url, { ...options, data: body as any })
  }

  /**
   * Выполняет PUT запрос
   */
  async put<T = unknown>(
    url: string,
    body?: Record<string, any> | BodyInit | null,
    options: ApiRequestOptions = {}
  ): Promise<T> {
    return this.request<T>('PUT', url, { ...options, data: body as any })
  }

  /**
   * Выполняет PATCH запрос
   */
  async patch<T = unknown>(
    url: string,
    body?: Record<string, any> | BodyInit | null,
    options: ApiRequestOptions = {}
  ): Promise<T> {
    return this.request<T>('PATCH', url, { ...options, data: body as any })
  }

  /**
   * Выполняет DELETE запрос
   */
  async delete<T = unknown>(
    url: string,
    options: ApiRequestOptions = {}
  ): Promise<T> {
    return this.request<T>('DELETE', url, options)
  }

  /**
   * Базовый метод для выполнения запросов
   */
  private async request<T = unknown>(
    method: string,
    url: string,
    options: ApiRequestOptions = {}
  ): Promise<T> {
    // Создаем AbortController если не передан
    const abortController = options.abortController || new AbortController()
    const signal = options.signal || abortController.signal

    // Определяем базовый URL
    let baseUrl = this.baseUrl
    if (options.endpoint && !baseUrl) {
      try {
        baseUrl = getApiBaseUrl(options.endpoint)
      } catch {
        // Если useRuntimeConfig недоступен, endpoint не будет использован
        throw new Error(`Cannot resolve endpoint ${options.endpoint}. useRuntimeConfig is not available.`)
      }
    }

    // Нормализуем baseUrl (убираем trailing slash)
    if (baseUrl && baseUrl.endsWith('/')) {
      baseUrl = baseUrl.slice(0, -1)
    }
    // Нормализуем url (добавляем leading slash если нужно)
    const normalizedUrl = url.startsWith('/') ? url : `/${url}`

    // Формируем полный абсолютный URL
    // Если baseUrl не указан, но url уже абсолютный - используем его
    let fullUrl: string
    if (baseUrl) {
      fullUrl = `${baseUrl}${normalizedUrl}`
    } else if (url.startsWith('http://') || url.startsWith('https://')) {
      // URL уже абсолютный
      fullUrl = url
    } else {
      // Относительный URL без baseUrl - это ошибка
      throw new Error(`Cannot make request: baseUrl is not set and URL "${url}" is not absolute`)
    }

    // Подготавливаем опции для запроса
    const axiosConfig: AxiosRequestConfig = {
      method: method.toLowerCase() as any,
      url: fullUrl,
      signal,
      headers: {
        ...this.defaultHeaders,
        ...options.headers,
      },
      params: options.params,
      data: options.data,
      timeout: options.timeout || this.timeout,
      ...options,
    }

    // Удаляем наши кастомные поля из опций
    delete (axiosConfig as any).abortController
    delete (axiosConfig as any).endpoint

    try {
      // Вызываем onRequest hook
      if (this.onRequest) {
        await this.onRequest(axiosConfig)
      }

      // Выполняем запрос с retry логикой
      const response = await this.executeWithRetry<T>(axiosConfig)

      // Вызываем onResponse hook
      if (this.onResponse) {
        await this.onResponse(response)
      }

      return response.data
    } catch (error) {
      // Обрабатываем ошибку
      const handledError = ErrorHandler.handle(error)

      // Вызываем onError hook
      if (this.onError) {
        await this.onError(handledError)
      }

      throw handledError
    }
  }

  /**
   * Выполняет запрос с поддержкой retry
   */
  private async executeWithRetry<T>(
    config: AxiosRequestConfig,
    attempt: number = 0
  ): Promise<AxiosResponse<T>> {
    try {
      // Удаляем baseURL из конфига, так как мы уже формируем полный URL
      const { baseURL, ...configWithoutBaseURL } = config
      
      return await axios.request<T>({
        ...configWithoutBaseURL,
        responseType: 'json',
      })
    } catch (error) {
      const axiosError = error as AxiosError
      
      // Не повторяем если запрос был отменен
      if (axiosError.code === 'ERR_CANCELED' || ErrorHandler.isAbortError(error)) {
        throw error
      }

      // Не повторяем если это клиентская ошибка (4xx)
      if (axiosError.response) {
        const statusCode = axiosError.response.status
        if (statusCode < 500) {
          throw error
        }
      }

      // Повторяем запрос если не достигли лимита
      if (attempt < this.retries) {
        await this.delay(this.retryDelay)
        return this.executeWithRetry<T>(config, attempt + 1)
      }

      throw error
    }
  }

  /**
   * Задержка для retry
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  /**
   * Отменяет все активные запросы (если используется общий AbortController)
   */
  abort(controller: AbortController): void {
    controller.abort()
  }
}

/**
 * Создает экземпляр API клиента по умолчанию
 * Используйте эту функцию в Vue контексте (composables, components)
 * где доступен useRuntimeConfig()
 */
export function createApiClient(config: ApiClientConfig = {}): ApiClient {
  return new ApiClient(config)
}

